import { Calendar, FileText, MapPin, Shield } from 'lucide-react';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { PoliceStatusSelector } from '@/components/police/forms/PoliceStatusSelector';
import { PoliceStatusBadge } from '@/components/police/PoliceStatusBadge';
import { getPoliceReportStatusConfig } from '@/components/police/utils/policeUtils';
import type {
  PoliceReportAttestationStatus,
  ReadPoliceReport,
} from '@/services/api/generated/policeReportsService';
import { useUpdatePoliceReportAttestationStatusMutation } from '@/services/api/police';
import { formatTaskDate } from '@/utils/taskDateUtils';

interface PoliceReportContextProps {
  policeReport: ReadPoliceReport;
  matterId?: string;
  onUpdate?: () => void;
}

export const PoliceReportContext: React.FC<PoliceReportContextProps> = ({
  policeReport,
  matterId,
  onUpdate,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [updateStatus, { isLoading }] =
    useUpdatePoliceReportAttestationStatusMutation();

  if (!policeReport) {
    return null;
  }

  const {
    id: reportId,
    police_department: department,
    report_identifier,
    reported_incident_date,
    reported_location,
    attestation_status,
  } = policeReport;

  const statusConfig = getPoliceReportStatusConfig(attestation_status);

  const handleStatusChange = async (
    newStatus: PoliceReportAttestationStatus,
  ) => {
    if (!matterId) {
      toast.error('Cannot update status: Matter ID not available');
      return;
    }

    try {
      await updateStatus({
        matterId,
        id: reportId,
        updatePoliceReportAttestationStatus: {
          attestation_status: newStatus,
        },
      }).unwrap();

      toast.success('Report status updated successfully');
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleViewReport = () => {
    if (matterId && reportId) {
      const newSearchParams = new URLSearchParams(searchParams);
      navigate(
        `/matters/${matterId}/police/${reportId}?${newSearchParams.toString()}`,
      );
    }
  };

  return (
    <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div
          className="flex items-center gap-2 min-w-0 cursor-pointer hover:underline"
          role="link"
          tabIndex={0}
          onClick={handleViewReport}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleViewReport();
            }
          }}
        >
          <Shield className="h-4 w-4 text-purple-500 shrink-0" />
          <span className="text-slate-900 font-medium truncate">
            {department
              ? `${department.name}${department.state_code ? ` (${department.state_code})` : ''}`
              : 'Police Report'}
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <PoliceStatusBadge
            status={attestation_status}
            showFullLabel={false}
            size="sm"
          />
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
        {report_identifier && (
          <div className="flex items-center gap-1 min-w-0 break-all col-span-2">
            <FileText className="h-3 w-3" />
            <span className="font-mono truncate">{report_identifier}</span>
          </div>
        )}

        {reported_incident_date && (
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <span>{formatTaskDate(reported_incident_date)}</span>
          </div>
        )}

        {reported_location && (
          <div className="flex items-center gap-1 min-w-0 truncate col-span-2">
            <MapPin className="h-3 w-3" />
            <span className="truncate">{reported_location}</span>
          </div>
        )}
      </div>

      {/* Status management */}
      <div className="border-t border-slate-100 pt-2 space-y-2">
        <p className="text-xs leading-relaxed text-slate-600">
          {statusConfig.description}
        </p>

        {matterId && (
          <PoliceStatusSelector
            currentStatus={attestation_status}
            onStatusChange={handleStatusChange}
            disabled={isLoading}
            showQuickActions
            size="sm"
          />
        )}
      </div>
    </div>
  );
};
